import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import openCv from '../openCv';
import AskKate from '../AskKate';
import CvNotes from '../CvNotes';
import CandidateHistory from '../CandidateHistory';

const API = 'https://carriera-backend.onrender.com/api';

// Steph's call sheet (candidate inventory Phase 2, 29 Oct 2026). Rows come from
// GET /api/screenings/to-call: Kate's Shortlist + Maybe not yet reached. What Steph
// records is saved by PATCH /api/screenings/:id/call, which also copies expected
// salary / notice / languages onto the candidate card. The backend refuses that
// route for Kate (role "agent"); the page just hides the buttons for her.
// Values below must match the check constraints in the backend migration
// 2026-10-29_candidate_inventory.sql.

const CALL_RESULTS = [
  { value: 'interested',     label: 'Interested' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'no_answer',      label: 'No answer' },
  { value: 'wrong_number',   label: 'Wrong number' },
  { value: 'not_looking',    label: 'Not looking' },
];

const WRONG_REASONS = [
  { value: 'wrong_experience', label: 'Wrong kind of experience' },
  { value: 'missed_must_have', label: 'Missed a must-have' },
  { value: 'level_wrong',      label: 'Level wrong' },
  { value: 'salary',           label: 'Salary out of range' },
  { value: 'location',         label: 'Location / travel' },
  { value: 'cv_misread',       label: 'CV misread' },
  { value: 'other',            label: 'Other' },
];

const NOT_INTERESTED_REASONS = ['Happy where I am', 'Salary', 'Location', 'Not looking until later'];

const INPUT = 'w-full border border-carriera-border px-3 py-2 text-sm focus:outline-none focus:border-carriera-accent';

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

function jobLabel(job) {
  if (!job) return 'No specific job';
  return job.clients?.company_name ? `${job.title} · ${job.clients.company_name}` : job.title;
}

// WhatsApp click-to-chat wants the number in international form, digits only:
// 012-345 6789 (or 12-345 6789) -> 60123456789. Returns '' when there is no usable number.
export function waNumber(phone) {
  let digits = (phone || '').replace(/\D/g, '');
  if (digits.startsWith('0')) digits = '6' + digits;
  else if (digits.startsWith('1') && digits.length <= 10) digits = '60' + digits;
  return digits.length >= 10 ? digits : '';
}

function Choice({ active, onClick, children, tone = 'neutral' }) {
  const on = tone === 'yes' ? 'bg-green-600 border-green-600 text-white'
    : tone === 'no' ? 'bg-red-500 border-red-500 text-white'
    : 'bg-carriera-text border-carriera-text text-white';
  return (
    <button type="button" onClick={onClick}
      className={`text-xs border px-3 py-1.5 transition-colors ${active ? on : 'border-carriera-border text-carriera-sec hover:border-carriera-accent'}`}>
      {children}
    </button>
  );
}

function Label({ children }) {
  return <p className="text-xs font-semibold text-carriera-muted uppercase tracking-widest mb-2">{children}</p>;
}

const BLANK_CALL = {
  call_result: '', not_interested_reason: '', kate_right: null, wrong_reason: '', wrong_note: '',
  expected_salary: '', notice_period: '', languages: '', call_notes: ''
};

function CallRow({ row, headers, canRecord, onSaved }) {
  const c = row.candidates;
  const [notesOpen, setNotesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [form, setForm] = useState(BLANK_CALL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const [closing, setClosing] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function closeRow() {
    setError('');
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/screenings/${row.id}/call`,
        { close: 'not_using', close_note: closeNote.trim() }, { headers });
      setSaved(data);
      setClosing(false);
      onSaved(row.id, data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close.');
    } finally {
      setSaving(false);
    }
  }

  const wa = waNumber(c.phone);
  const waLink = wa && row.wa_no_answer
    ? `https://wa.me/${wa}?text=${encodeURIComponent(row.wa_no_answer)}` : '';
  const guide = (row.call_guide || '').split('\n').map(l => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim()).filter(Boolean);

  async function save() {
    setError('');
    if (form.kate_right === false && !form.wrong_reason) {
      setError('Pick why Kate was wrong.');
      return;
    }
    const body = {};
    for (const k of ['call_result', 'not_interested_reason', 'wrong_note', 'notice_period', 'call_notes']) {
      if (form[k].trim()) body[k] = form[k].trim();
    }
    if (form.call_result !== 'not_interested') delete body.not_interested_reason;
    if (form.kate_right !== null) body.kate_right = form.kate_right;
    if (form.kate_right === false) body.wrong_reason = form.wrong_reason;
    else delete body.wrong_note;
    if (form.expected_salary.trim()) {
      // RM per month in full: "3k" or "3.8k" would otherwise be saved as RM3.
      const n = Number(form.expected_salary.replace(/[^\d.]/g, ''));
      if (!Number.isFinite(n) || n < 500 || /k/i.test(form.expected_salary)) {
        setError('Expected salary is RM per month in full, e.g. 3800.');
        return;
      }
      body.expected_salary = n;
    }
    const langs = form.languages.split(',').map(s => s.trim()).filter(Boolean);
    if (langs.length) body.languages = langs;
    if (!Object.keys(body).length) { setError('Nothing to save yet.'); return; }

    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/screenings/${row.id}/call`, body, { headers });
      setSaved(data);
      setForm(BLANK_CALL);
      onSaved(row.id, data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save the call.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-carriera-border p-5" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-serif text-lg text-carriera-text">{c.name}</p>
            <span className={`text-xs px-2 py-0.5 border rounded ${row.verdict === 'shortlist'
              ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
              {row.verdict === 'shortlist' ? 'Shortlist' : 'Maybe'}
            </span>
            {row.source === 'pool' && (
              <span className="text-xs px-2 py-0.5 border rounded bg-blue-50 text-blue-700 border-blue-200">Pool match</span>
            )}
            {row.call_attempts > 0 && (
              <span className="text-xs text-carriera-muted">
                Called {row.call_attempts}x, last {formatDate(row.called_at)}
              </span>
            )}
          </div>
          <p className="text-xs text-carriera-sec mt-1">{jobLabel(row.jobs)}</p>
          <p className="text-xs text-carriera-muted mt-0.5">
            {[c.phone, c.location].filter(Boolean).join(' · ') || 'No phone or location'}
            {' · '}screened {formatDate(row.screened_at)}
          </p>
        </div>
        <button onClick={() => openCv(c.id, headers)} disabled={!row.has_cv}
          title={row.has_cv ? 'Opens the CV (link works for 5 minutes)' : 'No CV stored'}
          className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
          {row.has_cv ? 'CV' : 'No CV stored'}
        </button>
        <button onClick={() => setNotesOpen(true)}
          title="CV with pen and highlighter beside the call notes form; Ready for profile"
          className="text-xs bg-carriera-text text-white px-3 py-1.5 hover:bg-carriera-accent transition-colors flex-shrink-0">
          CV & call notes
        </button>
      </div>
      {notesOpen && <CvNotes candidateId={c.id} screeningId={row.id} onClose={() => setNotesOpen(false)} />}

      <div className="grid grid-cols-2 gap-5 mb-4">
        <div className="space-y-3">
          {row.reason && (
            <div>
              <Label>Kate's reason</Label>
              <p className="text-sm text-carriera-sec leading-relaxed whitespace-pre-wrap">{row.reason}</p>
            </div>
          )}
          {c.kate_summary && (
            <div>
              <Label>Summary</Label>
              <p className="text-sm text-carriera-sec leading-relaxed whitespace-pre-wrap">{c.kate_summary}</p>
            </div>
          )}
        </div>
        <div>
          <Label>Call guide</Label>
          {guide.length ? (
            <ol className="list-decimal list-inside space-y-1">
              {guide.map((q, i) => <li key={i} className="text-sm text-carriera-sec">{q}</li>)}
            </ol>
          ) : <p className="text-xs text-carriera-muted">Kate left no questions.</p>}
          {(c.expected_salary || c.notice_period) && (
            <p className="text-xs text-carriera-muted mt-2">
              On file: {[c.expected_salary && `expects RM${Number(c.expected_salary).toLocaleString('en-MY')}`,
                c.notice_period && `notice ${c.notice_period}`].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-carriera-border pt-3 mb-4">
        <button onClick={() => setHistoryOpen(o => !o)}
          className="text-xs text-carriera-accent hover:underline">
          {historyOpen ? 'Hide history' : 'History: notes, past jobs, activity log'}
        </button>
        {historyOpen && (
          <div className="mt-3">
            <CandidateHistory candidateId={c.id} headers={headers} excludeScreeningId={row.id} />
          </div>
        )}
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 px-3 py-2 mb-3 text-xs text-green-700">
          Saved{saved.call_result ? `: ${CALL_RESULTS.find(r => r.value === saved.call_result)?.label}` : ''}
          {saved.kate_right === true && ', Kate right'}
          {saved.kate_right === false && ', Kate wrong'}
          {saved.call_closed_at && '. Closed: moves to Done on the next refresh (Undo there).'}
        </div>
      )}

      {!canRecord ? (
        <p className="text-xs text-carriera-muted border-t border-carriera-border pt-3">Only Ted or Steph record calls.</p>
      ) : saved?.call_closed_at ? null : (
        <div className="border-t border-carriera-border pt-4 space-y-4">
          <div>
            <Label>Call result</Label>
            <div className="flex gap-2 flex-wrap items-center">
              {CALL_RESULTS.map(r => (
                <Choice key={r.value} active={form.call_result === r.value}
                  onClick={() => set('call_result', form.call_result === r.value ? '' : r.value)}>
                  {r.label}
                </Choice>
              ))}
              {form.call_result === 'no_answer' && (waLink ? (
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                  className="text-xs bg-green-600 text-white px-3 py-1.5 hover:bg-green-700 transition-colors">
                  WhatsApp Kate's message
                </a>
              ) : (
                <span className="text-xs text-carriera-muted">
                  {wa ? 'Kate prepared no WhatsApp text.' : 'No usable phone number for WhatsApp.'}
                </span>
              ))}
            </div>
            {form.call_result === 'not_interested' && (
              <div className="flex gap-2 flex-wrap mt-2">
                {NOT_INTERESTED_REASONS.map(r => (
                  <Choice key={r} active={form.not_interested_reason === r} onClick={() => set('not_interested_reason', r)}>{r}</Choice>
                ))}
                <input className="border border-carriera-border px-2 py-1 text-xs focus:outline-none focus:border-carriera-accent flex-1 min-w-40"
                  placeholder="or type a reason" value={NOT_INTERESTED_REASONS.includes(form.not_interested_reason) ? '' : form.not_interested_reason}
                  onChange={e => set('not_interested_reason', e.target.value)} />
              </div>
            )}
          </div>

          <div>
            <Label>Kate right?</Label>
            <div className="flex gap-2">
              <Choice tone="yes" active={form.kate_right === true} onClick={() => set('kate_right', form.kate_right === true ? null : true)}>Yes</Choice>
              <Choice tone="no" active={form.kate_right === false} onClick={() => set('kate_right', form.kate_right === false ? null : false)}>No</Choice>
            </div>
            {form.kate_right === false && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  {WRONG_REASONS.map(r => (
                    <Choice key={r.value} active={form.wrong_reason === r.value} onClick={() => set('wrong_reason', r.value)}>{r.label}</Choice>
                  ))}
                </div>
                <input className={INPUT} placeholder="One line on what Kate missed (optional)"
                  value={form.wrong_note} onChange={e => set('wrong_note', e.target.value)} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Expected salary (RM)</Label>
              <input className={INPUT} inputMode="numeric" placeholder="e.g. 3800" value={form.expected_salary}
                onChange={e => set('expected_salary', e.target.value)} />
            </div>
            <div>
              <Label>Notice period</Label>
              <input className={INPUT} placeholder="e.g. 1 month" value={form.notice_period}
                onChange={e => set('notice_period', e.target.value)} />
            </div>
            <div>
              <Label>Languages (all of them)</Label>
              <input className={INPUT} placeholder="e.g. English, Malay, Mandarin" value={form.languages}
                title="Replaces the languages on the candidate's card, so list every language they speak" 
                onChange={e => set('languages', e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Call notes</Label>
            <textarea rows={3} className={INPUT + ' resize-y'} value={form.call_notes}
              onChange={e => set('call_notes', e.target.value)} />
          </div>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="text-xs bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
              {saving ? 'Saving...' : 'Save call'}
            </button>
            {!saved?.call_closed_at && !closing && (
              <button onClick={() => setClosing(true)}
                title="Steph's decision, separate from Kate right?: e.g. never answers, or a reason Kate could not know"
                className="text-xs border border-carriera-border px-3 py-2 text-carriera-sec hover:border-carriera-accent transition-colors">
                Close: not using this candidate
              </button>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
          {closing && (
            <div className="flex gap-2 items-center">
              <input className={INPUT} autoFocus maxLength={500} value={closeNote} onChange={e => setCloseNote(e.target.value)}
                placeholder="Why not (optional), e.g. no answer x3, client already saw him through another agency" />
              <button onClick={closeRow} disabled={saving}
                className="text-xs bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40 flex-shrink-0">
                Close
              </button>
              <button onClick={() => setClosing(false)} className="text-xs text-carriera-muted hover:text-carriera-text flex-shrink-0">Cancel</button>
            </div>
          )}
        </div>
      )}

      <div className="border-t border-carriera-border pt-3 mt-4">
        <AskKate aboutType="screening" aboutId={row.id} aboutLabel={`${c.name} · ${jobLabel(row.jobs)}`} startOpen={false}
          placeholder="e.g. Draft a WhatsApp asking for her latest payslip" />
      </div>
    </div>
  );
}

// Weekly reject check (candidate inventory Phase 3c, 31 Oct 2026). Kate's
// "Kate right?" only covers people she picked; this catches the ones she wrongly
// turned away. GET /api/screenings/reject-check gives up to 5 random rejects from
// last week (the same 5 all week); Steph's answer is saved with
// PATCH /api/screenings/:id/call { reject_check, wrong_note }. Kate turns every
// "Yes" into a lesson. ?week_of=YYYY-MM-DD in the page address looks at another week.
function RejectRow({ row, headers, canRecord, onSaved }) {
  const c = row.candidates || {};
  const [note, setNote] = useState(row.reject_check !== null ? row.wrong_note || '' : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function answer(value) {
    setError('');
    setSaving(true);
    try {
      const body = { reject_check: value };
      // A reject has no "Kate right?", so wrong_note holds the reason Kate should have picked them.
      body.wrong_note = value ? note.trim() : '';
      const { data } = await axios.patch(`${API}/screenings/${row.id}/call`, body, { headers });
      if (!value) setNote('');
      onSaved(row.id, data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const answered = row.reject_check !== null && row.reject_check !== undefined;
  return (
    <div className="bg-white border border-carriera-border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-serif text-base text-carriera-text">{c.name}</p>
          <p className="text-xs text-carriera-sec mt-0.5">{jobLabel(row.jobs)}</p>
          <p className="text-xs text-carriera-muted mt-0.5">
            {[c.location, (c.role_specialisation || []).join(', ')].filter(Boolean).join(' · ')}
            {' · '}screened {formatDate(row.screened_at)}
          </p>
        </div>
        <button onClick={() => openCv(c.id, headers)} disabled={!row.has_cv}
          className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
          {row.has_cv ? 'CV' : 'No CV stored'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-5 mt-3">
        <div>
          <Label>Why Kate rejected</Label>
          <p className="text-sm text-carriera-sec leading-relaxed whitespace-pre-wrap">{row.reason || '-'}</p>
          {row.better_fit_for && <p className="text-xs text-carriera-muted mt-1">Better fit for: {row.better_fit_for}</p>}
        </div>
        {c.kate_summary && (
          <div>
            <Label>Summary</Label>
            <p className="text-sm text-carriera-sec leading-relaxed whitespace-pre-wrap">{c.kate_summary}</p>
          </div>
        )}
      </div>
      <div className="border-t border-carriera-border pt-3 mt-3">
        <Label>Should Kate have picked this one?</Label>
        {!canRecord ? (
          <p className="text-xs text-carriera-muted">
            {answered ? `Answered: ${row.reject_check ? 'Yes' : 'No'}` : 'Only Ted or Steph answer this.'}
          </p>
        ) : (
          <div className="flex gap-2 items-center flex-wrap">
            <Choice tone="yes" active={row.reject_check === true} onClick={() => !saving && answer(true)}>Yes</Choice>
            <Choice tone="no" active={row.reject_check === false} onClick={() => !saving && answer(false)}>No</Choice>
            <input className="border border-carriera-border px-2 py-1 text-xs focus:outline-none focus:border-carriera-accent flex-1 min-w-40"
              placeholder="If Yes: one line on what Kate missed (optional, type before clicking Yes)"
              value={note} onChange={e => setNote(e.target.value)} />
            {saving && <span className="text-xs text-carriera-muted">Saving...</span>}
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function RejectCheck({ headers, canRecord }) {
  const [check, setCheck] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const weekOf = new URLSearchParams(window.location.search).get('week_of');
    axios.get(`${API}/screenings/reject-check`, { headers, params: weekOf ? { week_of: weekOf } : {} })
      .then(res => setCheck(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load the weekly reject check.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaved(id, data) {
    setCheck(ch => ({ ...ch, rows: ch.rows.map(r => r.id === id
      ? { ...r, reject_check: data.reject_check, wrong_note: data.wrong_note } : r) }));
  }

  if (error) return <p className="text-sm text-red-600 mb-6">{error}</p>;
  if (!check) return null;
  const left = check.rows.filter(r => r.reject_check === null || r.reject_check === undefined).length;

  return (
    <div className="mb-8">
      <button onClick={() => setOpen(o => !o)} className="w-full text-left flex items-end justify-between gap-4 mb-3">
        <div>
          <h3 className="font-serif text-xl text-carriera-text">Weekly reject check</h3>
          <p className="text-sm text-carriera-muted mt-0.5">
            {check.rows.length
              ? `${check.rows.length} of the ${check.rejects_in_week} people Kate rejected in the week of ${formatDate(check.drawn_from)}. ${left ? `${left} to answer.` : 'All answered, thank you.'}`
              : `Kate rejected nobody in the week of ${formatDate(check.drawn_from)}. Nothing to check this week.`}
          </p>
        </div>
        <span className="text-xs text-carriera-muted">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && check.rows.length > 0 && (
        <div className="space-y-3">
          {check.rows.map(r => (
            <RejectRow key={r.id} row={r} headers={headers} canRecord={canRecord} onSaved={handleSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

// Done tab (Ted, 3 Nov 2026): rows closed in the last 14 days and why, with Undo for a
// mis-click. Undo of a call result also clears the result; "Kate right?" is left alone.
function DoneRow({ row, headers, canRecord, onReopened }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const result = CALL_RESULTS.find(r => r.value === row.call_result)?.label;
  const why = row.call_closed_reason === 'not_using'
    ? `Not using${row.call_closed_note ? `: ${row.call_closed_note}` : ''}`
    : `Called: ${result || '-'}`;

  async function reopen() {
    setBusy(true);
    setError('');
    try {
      await axios.patch(`${API}/screenings/${row.id}/call`, { reopen: true }, { headers });
      onReopened(row.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to undo.');
      setBusy(false);
    }
  }

  return (
    <div className="bg-white border border-carriera-border px-5 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-carriera-text">
          {row.candidates.name}<span className="text-carriera-muted"> · {jobLabel(row.jobs)}</span>
        </p>
        <p className="text-xs text-carriera-sec mt-0.5">
          {why} · closed {formatDate(row.call_closed_at)}
          {row.kate_right === true && ' · Kate right'}
          {row.kate_right === false && ' · Kate wrong'}
        </p>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      {canRecord && (
        <button onClick={reopen} disabled={busy}
          className="text-xs border border-carriera-border px-3 py-1.5 text-carriera-sec hover:border-carriera-accent transition-colors disabled:opacity-40 flex-shrink-0">
          {busy ? 'Undoing...' : 'Undo: back to To call'}
        </button>
      )}
    </div>
  );
}

export default function ToCall({ onCount }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [tab, setTab] = useState('open');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canRecord = user.role !== 'agent';

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/screenings/to-call${tab === 'done' ? '?view=done' : ''}`, { headers });
      setRows(data);
      if (tab === 'open') onCount?.(data.length);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the To Call list.');
    } finally {
      setLoading(false);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Keep the saved row on screen (with its "Saved" note) until the next refresh,
  // so Steph sees what she recorded; only the call count is updated here.
  function handleSaved(id, data) {
    const next = rows.map(r => r.id === id
      ? { ...r, call_attempts: data.call_attempts, called_at: data.called_at, call_result: data.call_result,
          call_closed_at: data.call_closed_at } : r);
    setRows(next);
    onCount?.(next.filter(r => !r.call_closed_at).length);
  }

  function handleReopened(id) {
    setRows(rows.filter(r => r.id !== id));
  }

  const jobs = [...new Map(rows.filter(r => r.jobs).map(r => [r.jobs.id, r.jobs])).values()]
    .sort((a, b) => jobLabel(a).localeCompare(jobLabel(b)));
  // A job whose last row was dealt with disappears from the list on refresh; fall back to all jobs.
  const activeFilter = jobs.some(j => j.id === jobFilter) ? jobFilter : '';
  const shown = activeFilter ? rows.filter(r => r.jobs?.id === activeFilter) : rows;

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Recruitment</p>
          <h2 className="font-serif text-3xl font-normal text-carriera-text">To Call</h2>
          <p className="text-sm text-carriera-muted mt-1">
            Kate's Shortlist and Maybe. A row stays until Steph records a final call result or closes it.
          </p>
          <div className="flex gap-1 mt-3">
            {[['open', 'To call'], ['done', 'Done (last 14 days)']].map(([key, label]) => (
              <button key={key} onClick={() => { setTab(key); setJobFilter(''); }}
                className={`text-xs px-3 py-1.5 border transition-colors ${tab === key
                  ? 'bg-carriera-text border-carriera-text text-white' : 'border-carriera-border text-carriera-sec hover:border-carriera-accent bg-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <select className="border border-carriera-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-carriera-accent"
            value={activeFilter} onChange={e => setJobFilter(e.target.value)}>
            <option value="">All jobs ({rows.length})</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>{jobLabel(j)} ({rows.filter(r => r.jobs?.id === j.id).length})</option>
            ))}
          </select>
          <button onClick={fetchRows}
            className="text-xs border border-carriera-border px-3 py-2 text-carriera-sec hover:border-carriera-accent transition-colors bg-white">
            Refresh
          </button>
        </div>
      </div>

      {tab === 'open' && <RejectCheck headers={headers} canRecord={canRecord} />}

      {loading ? (
        <p className="text-sm text-carriera-muted py-8">Loading...</p>
      ) : error ? (
        <p className="text-sm text-red-600 py-8">{error}</p>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-carriera-border p-10 text-center">
          <p className="font-serif text-xl text-carriera-text mb-1">{tab === 'done' ? 'Nothing closed in the last 14 days' : 'Nobody to call'}</p>
          <p className="text-sm text-carriera-muted">
            {tab === 'done' ? 'Closed rows show here, with Undo.' : 'New names appear here after Kate screens applicants.'}
          </p>
        </div>
      ) : tab === 'done' ? (
        <div className="space-y-2">
          {shown.map(r => (
            <DoneRow key={r.id} row={r} headers={headers} canRecord={canRecord} onReopened={handleReopened} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map(r => (
            <CallRow key={r.id} row={r} headers={headers} canRecord={canRecord} onSaved={handleSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
